import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { USER_ROLE_LABEL } from '@apleona/shared';
import { api } from '../api';
import { F, FormGrid, Modal } from './Modal';

export interface UserFormValue {
  id?: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  role: string;
  companyId: string;
  locationId: string;
  active: boolean;
  password: string;
}

export const emptyUser = (): UserFormValue => ({
  email: '',
  firstName: '',
  lastName: '',
  phone: '',
  role: 'KOORDINATOR',
  companyId: '',
  locationId: '',
  active: true,
  password: '',
});

export const userToForm = (u: Record<string, unknown>): UserFormValue => ({
  ...emptyUser(),
  id: u.id as string,
  email: (u.email as string) ?? '',
  firstName: (u.firstName as string) ?? '',
  lastName: (u.lastName as string) ?? '',
  phone: (u.phone as string) ?? '',
  role: (u.role as string) ?? 'KOORDINATOR',
  companyId: ((u.company as { id?: string })?.id ?? '') as string,
  locationId: ((u.location as { id?: string })?.id ?? '') as string,
  active: (u.active as boolean) ?? true,
  password: '',
});

/** Sicheres Startpasswort — der Nutzer muss es beim ersten Login ändern. */
function suggestPassword(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint32Array(14));
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

export const UserForm: React.FC<{
  open: boolean;
  initial: UserFormValue;
  onClose: () => void;
}> = ({ open, initial, onClose }) => {
  const qc = useQueryClient();
  const [u, setU] = useState(initial);
  const [err, setErr] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  useEffect(() => {
    setU({ ...initial, password: initial.id ? '' : suggestPassword() });
    setErr(null);
    setCreated(null);
  }, [initial, open]);

  const { data: companies } = useQuery({
    queryKey: ['gesellschaften'],
    queryFn: () => api<{ id: string; name: string }[]>('/stammdaten/gesellschaften'),
    enabled: open,
  });
  const { data: locations } = useQuery({
    queryKey: ['standorte'],
    queryFn: () => api<{ id: string; name: string; company: { id: string } }[]>('/stammdaten/standorte'),
    enabled: open,
  });

  const set = (x: Partial<UserFormValue>) => setU((old) => ({ ...old, ...x }));

  const save = useMutation({
    mutationFn: async () => {
      const base = {
        email: u.email,
        firstName: u.firstName,
        lastName: u.lastName,
        phone: u.phone || null,
        role: u.role,
        companyId: u.companyId || null,
        locationId: u.locationId || null,
        active: u.active,
      };
      if (u.id) return api(`/benutzer/${u.id}`, { method: 'PATCH', body: JSON.stringify(base) });
      return api('/benutzer', { method: 'POST', body: JSON.stringify({ ...base, password: u.password }) });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['benutzer'] });
      if (u.id) onClose();
      else setCreated(u.password);
    },
    onError: (e: Error) => setErr(e.message),
  });

  const locOptions = (locations ?? []).filter((l) => !u.companyId || l.company.id === u.companyId);
  const valid = u.email.includes('@') && u.firstName.trim() && u.lastName.trim() && (u.id || u.password.length >= 10);

  if (created) {
    return (
      <Modal
        open={open}
        title="Benutzer angelegt"
        onClose={onClose}
        footer={<button className="btn primary" onClick={onClose}>Fertig</button>}
      >
        <div className="alert ok">
          Der Zugang wurde angelegt. Bitte das Startpasswort <b>jetzt</b> notieren – es wird
          nicht erneut angezeigt. Beim ersten Login muss es geändert werden.
        </div>
        <FormGrid cols={1}>
          <F label="E-Mail"><input readOnly value={u.email} /></F>
          <F label="Startpasswort">
            <input
              readOnly
              value={created}
              className="mono"
              onFocus={(e) => e.currentTarget.select()}
              style={{ fontSize: 16, fontWeight: 700 }}
            />
          </F>
        </FormGrid>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title={u.id ? `Benutzer bearbeiten – ${u.firstName} ${u.lastName}` : 'Neuen Benutzer anlegen'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Abbrechen</button>
          <button className="btn primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Speichern …' : 'Speichern'}
          </button>
        </>
      }
    >
      {err ? <div className="alert error">{err}</div> : null}
      <div className="alert info">
        Benutzer melden sich in der Tablet-App und im Portal an. Name, Standort, E-Mail und
        Telefon werden automatisch in jedes Protokoll übernommen.
      </div>

      <FormGrid cols={2}>
        <F label="Vorname *"><input value={u.firstName} onChange={(e) => set({ firstName: e.target.value })} /></F>
        <F label="Nachname *"><input value={u.lastName} onChange={(e) => set({ lastName: e.target.value })} /></F>
        <F label="E-Mail *" hint="Dient als Anmeldename">
          <input type="email" value={u.email} onChange={(e) => set({ email: e.target.value })} />
        </F>
        <F label="Telefon" hint="Erscheint als Ansprechpartner im Protokoll">
          <input value={u.phone} onChange={(e) => set({ phone: e.target.value })} />
        </F>
        <F label="Rolle">
          <select value={u.role} onChange={(e) => set({ role: e.target.value })}>
            {Object.entries(USER_ROLE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </F>
        <F label="Status">
          <select value={String(u.active)} onChange={(e) => set({ active: e.target.value === 'true' })}>
            <option value="true">aktiv</option>
            <option value="false">gesperrt</option>
          </select>
        </F>
        <F label="Gesellschaft">
          <select value={u.companyId} onChange={(e) => set({ companyId: e.target.value, locationId: '' })}>
            <option value="">—</option>
            {(companies ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </F>
        <F label="Standort / Niederlassung">
          <select value={u.locationId} onChange={(e) => set({ locationId: e.target.value })}>
            <option value="">—</option>
            {locOptions.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </F>

        {!u.id ? (
          <F label="Startpasswort *" span={2} hint="Mindestens 10 Zeichen. Muss beim ersten Login geändert werden.">
            <div className="row" style={{ flexWrap: 'nowrap' }}>
              <input className="mono" value={u.password} onChange={(e) => set({ password: e.target.value })} />
              <button className="btn sm" onClick={() => set({ password: suggestPassword() })}>Neu erzeugen</button>
            </div>
          </F>
        ) : null}
      </FormGrid>
    </Modal>
  );
};
